import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import ApolloError from '@shared/components/ApolloError';
import { showErrorToast, showSuccessToast } from '@shared/services/toastService';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import { DIALOG_MODES } from '@shared/components/confirmDialog/dialogWrappers/constants/DialogModes';
import { DialogActions, DialogContent } from '@mui/material';
import { useForm } from 'react-hook-form';
import {
  FORM_ID,
  MOVE_TEXT,
  CANCEL_TEXT,
  SAVE_TEXT,
  USER_MOVED_SUCCESSFULLY_MESSAGE,
  MOVE_CONFIRMATION_MESSAGE
} from './constants/moveLicenseConstants';
import CHANGE_USER_FREE_STATUS from '../../../queries/changeLicenseQueries/changeUserFreeStatusMutation';
import { MOVE_LICENSE_TYPES } from '../../../constants/MoveLicenseTypes';

/**
 * @typedef {object} MoveLicenseToFreeProps
 * @property {React.ReactNode} [dialogContent]
 * @property {React.ReactNode} [dialogActions]
 * @property {number} [externalUserId]
 * @property {boolean} [isFree]
 * @property {string} [previousLink]
 */

/**
 * @param {MoveLicenseToFreeProps} props
 */
const MoveLicenseToFree = props => {
  const { dialogContent, dialogActions, externalUserId, previousLink } = props;
  const navigate = useNavigate();
  const client = useApolloClient();

  const form = useForm();
  const { handleSubmit } = form;

  const [changeUserFreeStatus, { error: changeUserFreeStatusError }] = useMutation(
    CHANGE_USER_FREE_STATUS,
    {
      update: () => client.clearStore()
    }
  );

  const handleConfirm = async _ => {
    const result = await changeUserFreeStatus({
      variables: { moveLicenseType: MOVE_LICENSE_TYPES.FREE, externalUserId: externalUserId }
    });

    if (!result.data.changeUserFreeStatus.Success) {
      showErrorToast(result.data.changeUserFreeStatus.Errors.map(e => e.Message).join(', '));
      return;
    }

    showSuccessToast(USER_MOVED_SUCCESSFULLY_MESSAGE);

    navigate(previousLink);
  };

  if (changeUserFreeStatusError) {
    return <ApolloError />;
  }

  return (
    <form id={FORM_ID} onSubmit={handleSubmit(handleConfirm)}>
      <DialogContent>{dialogContent}</DialogContent>

      <DialogActions>
        {dialogActions}

        <ConfirmDialogWithButton
          openModalBtnText={SAVE_TEXT}
          confirmBtnText={MOVE_TEXT}
          cancelBtnText={CANCEL_TEXT}
          message={MOVE_CONFIRMATION_MESSAGE}
          dialogMode={DIALOG_MODES.EXTERNAL_FORM_SUBMIT}
          formId={FORM_ID}
        />
      </DialogActions>
    </form>
  );
};

export default MoveLicenseToFree;
